import {
  type AccessRequest,
  type ConsoleCompany,
  LICENSE_KIND_TITLES,
  type TenantCreated,
  TRIAL_DAYS,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, Copy, LogIn, Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { date, plural, relative } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast, unboundErrors } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { InviteResult } from '../admin/InviteResult';
import { EntryDialog, peopleText, usePlatformCan, usePlatformRole } from './console-shared';
import { limitsText, plansQuery, StateBadge } from './shared';

const TRANSLIT: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'c',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
  ә: 'a',
  ғ: 'g',
  қ: 'q',
  ң: 'n',
  ө: 'o',
  ұ: 'u',
  ү: 'u',
  һ: 'h',
  і: 'i',
};

/** Код компании из названия: «ТОО „Строй-Монтаж“» → stroy-montazh */
export function slugOf(name: string): string {
  const s = name
    .toLowerCase()
    .replace(/^(тоо|ао|ип|ооо|зао|пао|llp|llc|ltd|jsc)\s+/i, '')
    .split('')
    .map((ch) => TRANSLIT[ch] ?? ch)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return /^[a-z]/.test(s) ? s : s ? `c-${s}`.slice(0, 40) : '';
}

/** Создание компании (ЛЦ-01, ГЛ-04): запись компании, пробная лицензия и приглашение администратора */
export function CreateTenantDialog({
  request,
  onClose,
}: {
  request?: AccessRequest | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const plans = useQuery(plansQuery);
  const [f, setF] = useState({
    name: request?.company ?? '',
    slug: slugOf(request?.company ?? ''),
    slugTouched: false,
    adminEmail: request?.email ?? '',
    adminName: request?.name ?? '',
    planId: '',
    trialDays: String(TRIAL_DAYS),
    isDemo: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<TenantCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const activePlans = (plans.data ?? []).filter((p) => p.isActive);
  const planId = f.planId || activePlans[0]?.id || '';

  const save = useMutation({
    mutationFn: () =>
      api<TenantCreated>('/platform/tenants', {
        body: {
          name: f.name,
          slug: f.slug,
          adminEmail: f.adminEmail,
          adminName: f.adminName,
          planId,
          trialDays: Number(f.trialDays) || 0,
          requestId: request?.id ?? null,
          isDemo: f.isDemo,
        },
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      setCreated(res);
      toast('Компания создана');
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    save.mutate();
  }

  async function copy() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast('Не удалось скопировать — выделите ссылку и скопируйте вручную', 'danger');
    }
  }

  if (created) {
    return (
      <Dialog
        open
        // Закрывается только кнопкой: ссылка показывается один раз, случайный клик мимо окна её не спрячет
        onOpenChange={() => undefined}
        title={`Компания «${created.tenant.name}» создана`}
        description="Ссылку приглашения портал показывает один раз. Администратору уходит письмо с ней и инструкцией «С чего начать», если почта портала подключена."
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Закрыть
            </Button>
            <Link
              to="/platform/tenants/$id"
              params={{ id: created.tenant.id }}
              onClick={onClose}
              className="inline-flex h-11 items-center rounded-md bg-accent px-4 text-[14px] font-semibold text-on-accent md:h-10"
            >
              Открыть компанию
            </Link>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div>
            <div className="mb-1.5 text-[13px] font-medium text-ink">
              Приглашение администратора ({f.adminEmail})
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                readOnly
                value={created.inviteLink}
                onFocus={(e) => e.currentTarget.select()}
                className="font-mono text-[13px]"
              />
              <Button
                variant="secondary"
                icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                onClick={copy}
              >
                {copied ? 'Скопировано' : 'Скопировать'}
              </Button>
            </div>
            <p className="mt-1.5 text-[12px] text-ink-2">
              Ссылка действует 14 дней. Администратор примет приглашение по ней и дальше сам пригласит
              остальных сотрудников. Если ссылка потеряется или истечёт — в карточке компании есть «Новое
              приглашение».
            </p>
          </div>
          <InviteResult email={f.adminEmail} mail={created.mail} message={created.message} />
          <div className="rounded-md bg-surface-2 px-4 py-3 text-[13px] text-ink-2">
            Лицензия: {created.tenant.license ? LICENSE_KIND_TITLES[created.tenant.license.kind] : '—'}
            {created.tenant.license ? `, тариф «${created.tenant.license.planName}»` : ''}
            {created.tenant.license?.endsAt ? `, до ${date(created.tenant.license.endsAt)}` : ''}.
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Новая компания"
      description={
        request
          ? `По заявке от «${request.company}»`
          : 'Запись компании, пробная лицензия и приглашение администратора.'
      }
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="tenant-form"
            loading={save.isPending}
            disabled={!planId}
          >
            Создать компанию
          </Button>
        </>
      }
    >
      <form id="tenant-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field label="Название компании" htmlFor="t-name" error={errors.name} className="sm:col-span-2">
          <Input
            id="t-name"
            value={f.name}
            onChange={(e) =>
              setF((x) => ({
                ...x,
                name: e.target.value,
                slug: x.slugTouched ? x.slug : slugOf(e.target.value),
              }))
            }
            placeholder="ТОО «Название»"
            invalid={!!errors.name}
          />
        </Field>
        <Field
          label="Код компании"
          htmlFor="t-slug"
          hint="Латиница, цифры и дефис; виден в адресах и выгрузках"
          error={errors.slug}
        >
          <Input
            id="t-slug"
            value={f.slug}
            onChange={(e) => setF((x) => ({ ...x, slug: e.target.value.toLowerCase(), slugTouched: true }))}
            invalid={!!errors.slug}
          />
        </Field>
        <Field label="Тариф" htmlFor="t-plan" error={errors.planId}>
          <Select
            id="t-plan"
            value={planId}
            onChange={(e) => setF((x) => ({ ...x, planId: e.target.value }))}
          >
            {activePlans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {limitsText(p.limits)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Администратор компании — имя" htmlFor="t-admin-name" error={errors.adminName}>
          <Input
            id="t-admin-name"
            value={f.adminName}
            onChange={(e) => setF((x) => ({ ...x, adminName: e.target.value }))}
            invalid={!!errors.adminName}
          />
        </Field>
        <Field
          label="Администратор компании — почта"
          htmlFor="t-admin-email"
          hint="Получит приглашение владельцем компании"
          error={errors.adminEmail}
        >
          <Input
            id="t-admin-email"
            type="email"
            inputMode="email"
            value={f.adminEmail}
            onChange={(e) => setF((x) => ({ ...x, adminEmail: e.target.value }))}
            invalid={!!errors.adminEmail}
          />
        </Field>
        <Field
          label="Пробный период, дней"
          htmlFor="t-trial"
          hint="0 — сразу подписка на год по тарифу, без пробного периода"
          error={errors.trialDays}
        >
          <Input
            id="t-trial"
            type="number"
            inputMode="numeric"
            min={0}
            max={90}
            value={f.trialDays}
            onChange={(e) => setF((x) => ({ ...x, trialDays: e.target.value }))}
            invalid={!!errors.trialDays}
          />
        </Field>
        <Checkbox
          className="sm:col-span-2"
          label="Демо-компания"
          description="Для показа портала: внешние каналы (почта, Telegram, выгрузки наружу) выключены"
          checked={f.isDemo}
          onChange={(e) => setF((x) => ({ ...x, isDemo: e.target.checked }))}
        />
        <ErrorBox
          className="sm:col-span-2"
          error={
            Object.keys(errors).length
              ? unboundErrors(errors, ['name', 'slug', 'adminEmail', 'adminName', 'planId', 'trialDays'])
              : save.error
          }
        />
      </form>
    </Dialog>
  );
}

/** Срок лицензии в строке списка: «осталось 12 дн.», «сегодня», «просрочена 3 дн.» */
function daysText(daysLeft: number | null): string | null {
  if (daysLeft === null) return null;
  if (daysLeft === 0) return 'заканчивается сегодня';
  if (daysLeft > 0) return `осталось ${daysLeft} дн.`;
  return `просрочена ${-daysLeft} дн.`;
}

/**
 * Компании портала (ЛЦ-01, ЛЦ-03, ЛЦ-12): лицензия, люди и их активность, входы за неделю, «Войти».
 * Владельцу портала — ещё «Новая компания»; администратору портала — без тарифов и лицензий.
 */
export function TenantsPage() {
  const q = useQuery({
    queryKey: ['platform', 'companies'],
    queryFn: () => api<ConsoleCompany[]>('/platform/companies'),
  });
  const owner = usePlatformRole() === 'owner';
  const canEnter = usePlatformCan('entry');
  const me = useMe();
  const [creating, setCreating] = useState(false);
  const [entering, setEntering] = useState<{ id: string; name: string } | null>(null);
  const [filter, setFilter] = useState('');
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const needle = filter.trim().toLowerCase();
  const rows = needle
    ? q.data.filter((t) => t.name.toLowerCase().includes(needle) || t.slug.includes(needle))
    : q.data;
  const mine = new Set(me.tenants.map((t) => t.id));
  return (
    <>
      <Panel
        title="Компании"
        meta={`${q.data.length}`}
        actions={
          <>
            {q.data.length > 6 ? (
              <Input
                type="search"
                aria-label="Найти компанию"
                placeholder="Найти компанию"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full sm:w-56"
              />
            ) : null}
            {owner ? (
              <Button
                variant="primary"
                size="sm"
                icon={<Plus className="size-4" />}
                onClick={() => setCreating(true)}
              >
                Новая компания
              </Button>
            ) : null}
          </>
        }
        bodyClassName="p-0"
      >
        {!rows.length ? (
          <div className="p-5">
            <EmptyState title={needle ? 'Ничего не найдено' : 'Компаний пока нет'}>
              {needle
                ? 'Проверьте название или код компании.'
                : 'Создайте первую компанию или дождитесь заявки на доступ.'}
            </EmptyState>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((t) => {
              const days = daysText(t.daysLeft);
              const own = mine.has(t.id);
              return (
                <li key={t.id} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:gap-4">
                  <Link
                    to="/platform/tenants/$id"
                    params={{ id: t.id }}
                    className="-mx-2 flex min-h-11 min-w-0 flex-1 flex-col justify-center gap-0.5 rounded-md px-2 py-1 hover:bg-surface-2"
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink [overflow-wrap:anywhere]">{t.name}</span>
                      {t.isDemo ? <Badge tone="info">демо</Badge> : null}
                      {t.status !== 'active' ? (
                        <Badge tone="danger">{t.status === 'suspended' ? 'приостановлена' : t.status}</Badge>
                      ) : null}
                      {t.activeEntries ? (
                        <Badge tone="accent">внутри команда портала: {t.activeEntries}</Badge>
                      ) : null}
                    </span>
                    <span className="text-[13px] text-ink-2">
                      {t.planName ?? 'без лицензии'} · {peopleText(t.staff, t.external)} ·{' '}
                      {plural(t.projects, 'проект', 'проекта', 'проектов')}
                    </span>
                    <span className="text-[13px] text-ink-2">
                      Работали за 7 дней: <span className="num text-ink">{t.active7}</span>, за 30:{' '}
                      <span className="num text-ink">{t.active30}</span> · последний раз{' '}
                      {t.lastActivityAt ? relative(t.lastActivityAt) : 'больше месяца назад'}
                      {t.failed7 ? (
                        <span className="text-danger"> · неудачных входов за неделю: {t.failed7}</span>
                      ) : null}
                    </span>
                  </Link>
                  <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-2 sm:justify-end">
                    {days ? <span className="num">{days}</span> : null}
                    <StateBadge state={t.licenseState} />
                    {canEnter && !own ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<LogIn className="size-4" />}
                        onClick={() => setEntering({ id: t.id, name: t.name })}
                      >
                        Войти
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
      {creating ? <CreateTenantDialog onClose={() => setCreating(false)} /> : null}
      {entering ? <EntryDialog tenant={entering} onClose={() => setEntering(null)} /> : null}
    </>
  );
}
